import { Loader2 } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useReviewTranslation } from "@/domains/reviews/hooks/use-review-translation";
import type { SalonReviewSource } from "@/gql/graphql";
import { useSalonStore } from "@/lib/store/salon";
import { useSalonReviews } from "../hooks/use-salon-reviews";
import { useReviewUIStore } from "../store/use-review-ui-store";
import { SalonReviewRow } from "./salon-review-row";

interface SalonReviewListProps {
  /**
   * When set, restricts the list to one source (GOOGLE = GPA reviews,
   * MANUAL = in-app Platform reviews). Omit to show the combined feed.
   */
  source?: SalonReviewSource;
}

export function SalonReviewList({ source }: SalonReviewListProps = {}) {
  const { t } = useTranslation("settings");
  const { reviews, loading, loadingMore, hasMore, loadMore } = useSalonReviews({ source });
  const { starFilter, replyFilter, clearFilters } = useReviewUIStore();
  const { currentSalon } = useSalonStore();
  const { translateInBackground } = useReviewTranslation();

  // Fire-and-forget translation for Google reviews (≤3★, unreplied, unchecked).
  // Non-blocking — the list renders immediately; translations arrive via refetch.
  useEffect(() => {
    if (currentSalon && reviews.length > 0) {
      translateInBackground(reviews, currentSalon.id);
    }
  }, [currentSalon, reviews, translateInBackground]);

  const hasActiveFilter = starFilter !== null || replyFilter !== "all";

  if (loading && reviews.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (reviews.length === 0) {
    if (hasActiveFilter) {
      return (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground space-y-3">
          <p>No reviews match the current filters.</p>
          <Button size="sm" variant="outline" onClick={clearFilters}>
            Clear filters
          </Button>
        </div>
      );
    }
    return (
      <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
        {t("reviews.empty")}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {reviews.map((review) => (
        <SalonReviewRow key={review.id} review={review} />
      ))}

      {hasMore ? (
        <div className="flex justify-center pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void loadMore()}
            disabled={loadingMore}
          >
            {loadingMore ? (
              <>
                <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                Loading…
              </>
            ) : (
              "Load more"
            )}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
